import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import StatsCard from '../components/StatsCard';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FolderKanban,
  Play,
  CheckCircle2,
  XCircle,
  PlusCircle,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function DashboardPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    try {
      const res = await runsAPI.list(20, 0);
      setRuns(res.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const total = runs.length;
  const running = runs.filter((r) => r.status === 'running').length;
  const completed = runs.filter((r) => r.status === 'completed').length;
  const failed = runs.filter((r) => r.status === 'failed').length;
  const recent = runs.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Bienvenue */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Bonjour{user?.email ? `, ${user.email.split('@')[0]}` : ''}
          </h2>
          <p className="text-sm text-muted-foreground">
            Voici un résumé de vos projets
          </p>
        </div>
        <Button onClick={() => navigate('/projects/new')} className="glow-primary">
          <PlusCircle className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total projets" value={total} icon={FolderKanban} />
        <StatsCard title="En cours" value={running} icon={Play} />
        <StatsCard title="Terminés" value={completed} icon={CheckCircle2} />
        <StatsCard title="Échoués" value={failed} icon={XCircle} />
      </div>

      {/* Derniers projets */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Projets récents</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} className="text-xs">
            Voir tout
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : recent.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderKanban className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun projet pour le moment</p>
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/projects/new')}
                className="mt-2"
              >
                Créer votre premier projet
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recent.map((run) => (
                <button
                  key={run.id}
                  onClick={() => navigate(`/projects/${run.id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-accent/50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {run.goal?.substring(0, 80) || 'Sans titre'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {run.stack} &middot;{' '}
                      {run.created_at
                        ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: fr })
                        : ''}
                    </p>
                  </div>
                  <StatusBadge status={run.status} size="sm" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
